import YandexMapLoader from "../../vg/modules/maps/YandexMap";
import VGMap from "../../vg/modules/maps/vgmap";
import Request from "./mode/Request";
import Data from "./mode/Data";
import {
	addClass,
	find,
	removeClass,
	phoneCall,
	letterRatio,
	transliterate,
	isEmptyObj
} from "../../../../modules/vegas/js/util";

const PARAMS_DEFAULT = {
	view: '',
	company: '',
	point: '',
	servicing_offices: '',
	points: {
		get: '',
		set: '',
	}
};

class Points extends Request {
	constructor(element, params = {}) {
		super(element, params);
		this.points = [];
		this._visiblePoints = [];
		this._scope = this.element.closest('.points') || this.element.parentElement;
		this._pointsContainer = this._scope.querySelector('[data-points-list]');
		this._form = this._scope.querySelector('[data-points-search]');
		this._input = this._form?.querySelector('[name="search"]');
		this._searchQuery = '';
		this._view = this.element.dataset.initialView || 'all';
		this._map = null;
		this._descriptionTimer = null;
		this._bindEvents();

		Data.set(this.element, 'points', this);
	}

	static get Default() {
		return PARAMS_DEFAULT
	}

	static getInstance(element) {
		return Data.get(find(element), 'points');
	}

	static getOrCreateInstance(element, params = {}) {
		return this.getInstance(element) || new this(element, !isEmptyObj(params) ? params : {})
	}

	request(params = {}, {useCache = false} = {}) {
		this.params = this.getParams(params, this.element);
		const requestId = this._requestId = (this._requestId || 0) + 1;
		this._loadFailed = false;
		if (!useCache) this._viewCache = new Map();
		const cached = useCache && this._viewCache?.get(this._viewCacheKey());
		if (cached) {
			this._setLoading(false);
			this.map(cached.points, cached.companies);
			return Promise.resolve();
		}
		this._setLoading(true);
		this._setPanelDescription('hide');
		this._showListMessage('Загружаем пункты выдачи…');
		if (!this._map) this._showMapMessage('Загружаем карту…', true);
		// Карта загружается параллельно с запросом пунктов, список не ждёт её готовности.
		new YandexMapLoader().load();

		return this.run((points, companies) => {
			if (requestId !== this._requestId) return;
			this.map(points, companies);
			if (this.params.view) {
				this._viewCache ||= new Map();
				this._viewCache.set(this._viewCacheKey(), {points, companies});
				this._viewCache.set(this._viewCacheKey({...this.params, view: this._view}), {points, companies});
			}
		}).catch(() => {
			if (requestId !== this._requestId) return;
			this._loadFailed = true;
			this._showListMessage(this.params.view
				? 'Не удалось загрузить пункты. Нажмите на вкладку ещё раз.'
				: 'Не удалось загрузить пункты. Попробуйте открыть карту ещё раз или обновить страницу.');
			if (!this._map) this._showMapMessage('Не удалось загрузить пункты для карты. Обновите страницу.');
		}).finally(() => {
			if (requestId === this._requestId) this._setLoading(false);
		});
	}

	_viewCacheKey(params = this.params) {
		return JSON.stringify([params.view, params.company || '', params.point || '', params.servicing_offices || '']);
	}

	_setLoading(loading) {
		this._loadingView = loading
			? (this.params.view === 'auto' ? (this._view || this.element.dataset.initialView || 'all') : this.params.view)
			: null;
		this._loading = loading;
		if (loading) this._scope.setAttribute('aria-busy', 'true');
		else this._scope.removeAttribute('aria-busy');
		this._scope.classList.toggle('points--loading', loading);
		this._updateView();
	}

	map(points, companies) {
		const reuseMap = Boolean(this.params.view && this._map && this._mapParams);
		if (!reuseMap) {
			this._map?.destroy();
			this._map = null;
			this._mapParams = null;
			this._placemarks = [];
			this._mapPointIndices = [];
			this._companyControl = null;
		}
		this.points = points;
		this._selectedPoints = points;
		this._visiblePoints = points;
		this._companies = companies;
		this._origin = companies[0]?.origin || null;
		this._nearestLimit = companies[0]?.nearest_limit || 10;
		this._view = this._resolveView(companies);
		this._selectedCompany = '';
		this._searchQuery = '';
		if (this._input) this._input.value = '';
		this._updateOrigin();
		this._updateView();
		this._setPanelDescription('hide');
		if (reuseMap) this._setButtonListPointCompany(companies, this._map);
		this._applySearch();
		this._scope.classList.remove('d-none');
		this._scope.querySelector('.search-area--empty')?.classList.add('d-none');
		this._form?.querySelector('[data-points-search-clear]')?.classList.add('d-none');
		this._form?.querySelector('i')?.classList.remove('d-none');
		if (reuseMap) return;

		const firstPoint = points.find(point => this._coordinates(point));
		const center = this._origin?.coordinates || (firstPoint && this._coordinates(firstPoint));
		if (!center) {
			this._showMapMessage('Нет пунктов с координатами для отображения на карте.');
			return;
		}
		this._showMapMessage('Загружаем карту…', true);

		const requestId = this._requestId;
		new YandexMapLoader().load(() => {
			if (requestId !== this._requestId) return;
			this.element.querySelector('.points-map-message')?.remove();
			VGMap.init(this.element, {
				// Пункты передаются после создания карты, без повторного копирования большого массива.
				points: [],
				lat: center[0],
				lon: center[1],
				zoom: 12,
				maxZoom: 18,
				fitPointsBounds: false,
				mark: 'points',
				pointDetailsLabel: this.params.servicing_offices ? 'Подробнее об отделении' : 'Подробнее о пункте',
				marker: {type: 'custom'},
				permission: {isClustered: true},
				callback: {
					afterInitMap: (map, params, self) => {
						if (requestId !== this._requestId) {
							map.destroy();
							return;
						}
						this.element.removeAttribute('aria-busy');
						this._setButtonListPointCompany(companies, map, params, self);
						this.search(params, map, self);
					}
				}
			});
		}).then(ready => {
			if (ready === false && requestId === this._requestId) this._showMapMessage('Не удалось загрузить карту. Выберите пункт из списка или обновите страницу.');
		}).catch(() => {
			if (requestId === this._requestId) this._showMapMessage('Не удалось загрузить карту. Выберите пункт из списка или обновите страницу.');
		});
	}

	_bindEvents() {
		this._pointsContainer?.addEventListener('scroll', () => {
			const container = this._pointsContainer;
			if (container.scrollHeight - container.scrollTop - container.clientHeight < 400) this._appendListBatch();
		}, {passive: true});
		this._input?.addEventListener('input', () => this._applySearch());
		this._form?.addEventListener('submit', event => {
			event.preventDefault();
			this._applySearch();
		});
		this._form?.querySelector('[data-points-search-clear]')?.addEventListener('click', () => {
			this._input.value = '';
			this._applySearch();
			this._input.focus();
		});
		this._scope.addEventListener('click', event => {
			const view = event.target.closest('[data-points-view]');
			if (view) {
				this._selectView(view.dataset.pointsView, view);
				return;
			}
			const company = event.target.closest('[data-points-company]');
			if (company) {
				this._selectCompany(company.dataset.pointsCompany);
				this._pointsContainer.querySelector('[data-points-company=""]')?.focus({preventScroll: true});
				return;
			}
			const point = event.target.closest('[data-vg-toggle="map-point"], [data-vg-toggle="map-point-details"]');
			if (point) {
				event.preventDefault();
				this._selectPoint(Number(point.dataset.index));
			}
		});
		this._scope.querySelector('[data-points-close]')?.addEventListener('click', () => {
			this._setPanelDescription('hide');
			this._pointsContainer.querySelector('.point.active')?.focus({preventScroll: true});
		});
		this._scope.addEventListener('keydown', event => {
			if (event.key === 'Escape' && this._scope.querySelector('[data-points-description].show')) {
				this._setPanelDescription('hide');
				this._pointsContainer.querySelector('.point.active')?.focus({preventScroll: true});
			}
		});
	}

	search(params, map, self) {
		this._mapParams = params;
		this._map = map;
		this._mapHelper = self;
		this._applySearch();
	}

	_resolveView(companies) {
		const view = this.params.view === 'auto' ? companies[0]?.view : this.params.view;
		return view === 'nearby' && this._origin && !this.params.point && !this.params.servicing_offices ? 'nearby' : 'all';
	}

	_selectView(view, button) {
		if (view === 'nearby' && !this._origin) {
			const modal = document.getElementById('modal-location');
			if (modal) bootstrap.Modal.getOrCreateInstance(modal).show(button);
			return;
		}
		if (view === this._view && !this.params?.point && !this._loadFailed) return;
		this.request({view, point: ''}, {useCache: true});
	}

	_updateView() {
		const view = this._loadingView || this._view;
		this._scope.querySelectorAll('[data-points-view]').forEach(button => {
			const active = button.dataset.pointsView === view;
			const loading = Boolean(this._loading) && button.dataset.pointsView === this._loadingView;
			button.classList.toggle('active', active);
			button.querySelector('[data-points-view-label]')?.classList.toggle('invisible', loading);
			button.querySelector('[data-points-view-spinner]')?.classList.toggle('d-none', !loading);
			button.setAttribute('aria-pressed', String(active));
			button.setAttribute('aria-busy', String(loading));
			button.disabled = Boolean(this._loading);
		});
		this._form?.closest('.search-area')?.classList.toggle('d-none', view === 'nearby');
	}

	_applySearch() {
		this._updateFallbackNote();
		this._searchQuery = this._view === 'nearby' ? '' : (this._input?.value || '').trim().toLowerCase();
		const words = this._searchQuery.split(/\s+/).filter(Boolean).map(word => [
			word, transliterate(word, false), transliterate(word, true), letterRatio(word), letterRatio(word, true),
		]);
		let points = (this._selectedPoints || []).filter(point => {
			const text = [point.postcode, point.address, point.companyTitle].filter(Boolean).join(' ').toLowerCase();
			return words.every(variants => variants.some(word => text.includes(word)));
		});
		if (this._view === 'nearby') {
			// В списке допускаются повторы между блоками, на карте каждый пункт один.
			points = [...new Set(this._getListGroups(points).flatMap(group => group.points))]
				.sort((a, b) => a.distance_meters - b.distance_meters);
		}
		this._scope.querySelector('.search-area--empty')?.classList.toggle('d-none', !this._searchQuery || points.length > 0);
		this._form?.querySelector('[data-points-search-clear]')?.classList.toggle('d-none', !this._input?.value);
		this._form?.querySelector('i')?.classList.toggle('d-none', Boolean(this._input?.value));
		this._visiblePoints = points;
		if (this._mapParams) {
			this._mapParams.points = points;
			this._setPlaceMarks(this._mapParams, this._map, this._mapHelper);
		} else {
			this._setListPoints(points, this._pointsContainer);
		}
		if (this._pointsContainer) this._pointsContainer.scrollTop = 0;
	}

	_updateOrigin() {
		const container = this._scope.querySelector('[data-points-origin]');
		if (!container) return;
		container.querySelector('[data-points-origin-label]').textContent = this._origin?.type === 'city'
			? 'Населённый пункт' : (this._origin ? 'Ваш адрес' : 'Ваш город');
		const address = container.querySelector('[data-points-origin-address]');
		this._cityTitle = this._companies[0]?.city_title || this._cityTitle || address.textContent;
		const headerAddress = this._scope.ownerDocument
			?.querySelector('[data-current-address-link] [data-current-address]')
			?.textContent.trim();
		address.textContent = headerAddress || this._origin?.address || this._cityTitle;
		address.parentElement.setAttribute('title', address.textContent);
		this._updateFallbackNote();
	}

	_coordinates(point) {
		if (point.lat === null || point.lon === null || point.lat === '' || point.lon === '') return null;
		const lat = Number(point.lat), lon = Number(point.lon);
		return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && (lat !== 0 || lon !== 0) ? [lat, lon] : null;
	}

	_formatDistance(meters) {
		if (meters === null || meters === undefined || !Number.isFinite(meters)) return '';
		if (meters < 1000) return '≈ ' + Math.max(10, Math.round(meters / 10) * 10) + ' м';
		return '≈ ' + (meters / 1000).toLocaleString('ru-RU', {maximumFractionDigits: 1}) + ' км';
	}

	_showListMessage(message) {
		if (!this._pointsContainer) return;
		this._listBatch = null;
		this._scope.classList.remove('d-none');
		const text = document.createElement('p');
		text.className = 'points-list-message';
		text.setAttribute('role', 'status');
		text.textContent = message;
		this._pointsContainer.replaceChildren(text);
	}

	_showMapMessage(message, loading = false) {
		const text = document.createElement('div');
		text.className = 'points-map-message';
		text.setAttribute('role', 'status');
		text.textContent = message;
		this.element.replaceChildren(text);
		this.element.setAttribute('aria-busy', String(loading));
	}

	_getListGroups(points) {
		const groups = [];
		const nearby = this._view === 'nearby' && this._origin ? points.filter(point => Number.isFinite(point.distance_meters)).slice(0, this._nearestLimit) : [];
		if (nearby.length) {
			groups.push({name: 'nearby', title: this._origin.type === 'city'
				? 'Ближайшие к населённому пункту перевозчики' : 'Ближайшие 10 перевозчиков', points: nearby});
			const companies = this._companies.map(company => {
				const all = points.filter(point => point.company === company.name);
				return {
					name: company.name, title: company.title,
					points: all.filter(point => Number.isFinite(point.distance_meters)).slice(0, 1)
				};
			}).filter(company => company.points.length);
			const transports = companies.flatMap(company => company.points);
			if (transports.length) groups.push({
				name: 'transport',
				title: 'Перевозчики на минимальном расстоянии',
				points: transports,
				companies
			});
		} else {
			const company = this._companies.find(company => company.name === this._selectedCompany);
			groups.push({
				name: 'all',
				title: this._searchQuery ? 'Результаты поиска' : (company?.title || 'Пункты выдачи в городе'),
				points
			});
		}
		return groups;
	}

	_fallbackMessages() {
		const selectedCompany = this._selectedCompany || this.params?.company;
		const cities = new Map();
		this._companies
			.filter(company => company.fallback_city_title && (!selectedCompany || company.name === selectedCompany))
			.forEach(company => {
				const titles = cities.get(company.fallback_city_title) || [];
				titles.push(company.title);
				cities.set(company.fallback_city_title, titles);
			});
		return Array.from(cities, ([city, titles]) => {
			const services = titles.length > 1 ? `${titles.slice(0, -1).join(', ')} и ${titles[titles.length - 1]}` : titles[0];
			return `${services} — показаны пункты в ${city}`;
		});
	}

	_updateFallbackNote() {
		const note = this._scope.querySelector('[data-points-origin-fallback]');
		if (!note) return;
		if (this._view === 'nearby') {
			const messages = [];
			if (this._companies[0]?.nearby_fallback) messages.push('В выбранном населённом пункте нет доступных точек. Показываем ближайшие пункты в других населённых пунктах.');
			note.textContent = messages.join(' ');
		} else {
			note.textContent = this._fallbackMessages().join('; ');
		}
		note.hidden = !note.textContent;
	}

	_setListPoints(points, container) {
		if (!container) return;
		this._listBatch = null;
		container.replaceChildren();
		if (this._selectedCompany && !this.params.company) {
			const back = document.createElement('button');
			back.type = 'button';
			back.className = 'points-company-all';
			back.dataset.pointsCompany = '';
			back.textContent = 'Пункты выдачи в городе';
			container.append(back);
		}
		if (!points.length) {
			const text = document.createElement('p');
			text.className = 'points-list-message';
			text.setAttribute('role', 'status');
			text.textContent = this._searchQuery ? 'Измените запрос или очистите поиск.' : (this._view === 'nearby' ? 'Не удалось найти пункты с координатами для вашего адреса.' : 'В этом городе нет доступных пунктов выбранной службы.');
			container.append(text);
			return;
		}

		const indices = new Map(points.map((point, index) => [point, index]));
		const groups = this._getListGroups(points);

		groups.forEach(group => {
			const section = document.createElement(group.title ? 'section' : 'div');
			section.className = 'points-group points-group--' + group.name;
			section.dataset.pointsGroup = group.name;
			if (group.title) {
				const heading = document.createElement('h5');
				heading.className = 'points-group__title';
				heading.id = this.element.id + '-group-' + group.name;
				heading.textContent = group.title;
				section.setAttribute('aria-labelledby', heading.id);
				const count = document.createElement('span');
				count.className = 'points-group__count';
				count.textContent = group.points.length;
				heading.append(count);
				section.append(heading);
			}
			if (group.companies) {
				group.companies.forEach(company => {
					company.points.forEach(point => section.append(this._createPointCard(point, indices.get(point), true)));
				});
			} else {
				// Длинный список дополняется при прокрутке, поиск использует все загруженные пункты.
				this._listBatch = {section, points: group.points, indices, cursor: 0, rendered: new Map()};
				this._appendListBatch();
			}
			container.append(section);
		});
	}

	_appendListBatch() {
		const batch = this._listBatch;
		if (!batch) return;
		const end = Math.min(batch.cursor + 60, batch.points.length);
		const fragment = document.createDocumentFragment();
		let anchor = null;
		for (; batch.cursor < end; batch.cursor++) {
			const point = batch.points[batch.cursor];
			const index = batch.indices.get(point);
			if (batch.rendered.has(index)) {
				batch.section.insertBefore(fragment, batch.rendered.get(index));
				continue;
			}
			const card = this._createPointCard(point, index, false);
			batch.rendered.set(index, card);
			fragment.append(card);
		}
		// Выбранный на карте пункт может быть уже добавлен дальше текущей порции.
		const next = batch.points.slice(end).find(point => batch.rendered.has(batch.indices.get(point)));
		if (next) anchor = batch.rendered.get(batch.indices.get(next));
		batch.section.insertBefore(fragment, anchor);
	}

	_ensurePointCard(index) {
		const batch = this._listBatch;
		const point = this._visiblePoints[index];
		if (!batch || batch.rendered.has(index) || !batch.points.includes(point)) return;
		const card = this._createPointCard(point, index, false);
		const next = [...batch.section.querySelectorAll('.point')].find(card => Number(card.dataset.index) > index);
		batch.section.insertBefore(card, next || null);
		batch.rendered.set(index, card);
	}

	_createPointCard(point, index, isTransport) {
		const card = document.createElement('button');
		card.type = 'button';
		card.className = 'point' + (isTransport ? ' point--transport' : '');
		card.dataset.vgToggle = 'map-point';
		card.dataset.index = index;
		card.setAttribute('aria-pressed', 'false');
		const address = document.createElement('span');
		address.className = 'address';
		address.textContent = point.address;
		if (this._view === 'nearby' && point.city_title) {
			const city = document.createElement('span');
			city.className = 'point__city';
			city.textContent = point.city_title;
			address.prepend(city);
		}
		const company = document.createElement('span');
		company.className = 'company';
		const image = document.createElement('span');
		image.className = 'image';
		const img = document.createElement('img');
		img.src = point.image;
		img.alt = '';
		image.append(img);
		const name = document.createElement('span');
		name.className = 'name';
		name.textContent = isTransport ? point.companyTitle : point.title;
		company.append(image, name);
		const meta = document.createElement('span');
		meta.className = 'point__meta';
		meta.append(company);
		const distance = this._formatDistance(point.distance_meters);
		if (distance) {
			const badge = document.createElement('span');
			badge.className = 'point__distance';
			badge.textContent = distance;
			badge.title = 'Расстояние от вашего адреса';
			meta.append(badge);
		}
		if (isTransport) card.append(meta, address);
		else card.append(address, meta);
		return card;
	}

	_setPlaceMarks(params, map, self) {
		this._visiblePoints = params.points;
		this._setPanelDescription('hide');
		this._setListPoints(params.points, this._pointsContainer);
		map.geoObjects.removeAll();
		const mapPoints = params.points.filter(point => this._coordinates(point));
		const indices = new Map(params.points.map((point, index) => [point, index]));
		mapPoints.forEach(point => {
			point.listIndex = indices.get(point);
		});
		this._placemarks = mapPoints.length ? self.PlaceMark.set(map, {...params, points: mapPoints}) : [];
		this._mapPointIndices = mapPoints.map(point => indices.get(point));
		this._placemarks.forEach((placemark, i) => {
			placemark.events.add('click', () => this._selectPoint(this._mapPointIndices[i]));
		});
		if (this._origin) {
			const originLabel = this._origin.type === 'city' ? 'Центр населённого пункта' : 'Ваш адрес';
			map.geoObjects.add(new ymaps.Placemark(this._origin.coordinates, {
				hintContent: originLabel
			}, {
				preset: 'islands#redIcon', zIndex: 1000,
			}));
		}
		this._fitBounds(mapPoints);
		if (this.params.point && params.points.length === 1 && !this._searchQuery) this._selectPoint(0, false);
	}

	_fitBounds(mapPoints) {
		let points = mapPoints;
		if (this._view === 'nearby' && this._origin) {
			points = points.filter(point => Number.isFinite(point.distance_meters)).slice(0, this._nearestLimit);
		}
		const coordinates = points.map(point => this._coordinates(point));
		if (this._view === 'nearby' && this._origin) coordinates.push(this._origin.coordinates);
		if (!coordinates.length) return;
		const latitudes = coordinates.map(point => point[0]), longitudes = coordinates.map(point => point[1]);
		const bounds = [[Math.min(...latitudes), Math.min(...longitudes)], [Math.max(...latitudes), Math.max(...longitudes)]];
		if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) {
			this._map.setCenter(coordinates[0], 16);
		} else {
			this._map.setBounds(bounds, {checkZoomRange: true, zoomMargin: 40});
		}
	}

	_selectPoint(index, focusClose = true) {
		const point = this._visiblePoints[index];
		if (!point) return;
		this._ensurePointCard(index);
		this._placemarks?.forEach((placemark, i) => {
			const item = this._visiblePoints[this._mapPointIndices[i]];
			const marker = this._mapPointIndices[i] === index ? item.markerActive : item.marker;
			placemark.options.set('iconImageHref', marker.src);
			placemark.options.set('iconImageSize', marker.size);
			placemark.options.set('iconImageOffset', marker.offset);
		});
		const coordinates = this._coordinates(point);
		if (coordinates && this._map) this._map.setCenter(coordinates, 17);
		const cards = this._pointsContainer.querySelectorAll('[data-vg-toggle="map-point"]');
		cards.forEach(card => {
			const active = Number(card.dataset.index) === index;
			card.classList.toggle('active', active);
			card.setAttribute('aria-pressed', String(active));
		});
		const card = [...cards].find(card => Number(card.dataset.index) === index);
		if (card) this._setPanelDescription('show', card, focusClose);
	}

	_selectCompany(name) {
		this._selectedCompany = name;
		const company = this._companies.find(company => company.name === name);
		this._selectedPoints = name ? this.points.filter(point => point.company === name) : this.points;
		this._companyControl?.data.set('title', company?.title || 'Перевозчики');
		this._applySearch();
	}

	_setButtonListPointCompany(companies, map) {
		if (this._companyControl) map.controls.remove(this._companyControl);
		this._companyControl = null;
		if (!companies?.length || this.params.company) return;
		const instance = this;
		const controlId = this.element.id + '-company-control';
		const layout = ymaps.templateLayoutFactory.createClass(
			'<button type="button" id="' + controlId + '" class="btn btn-primary btn-sm" data-bs-toggle="dropdown">' +
			'{{data.title}} <span class="caret"></span></button>' +
			'<ul class="points-companies-list dropdown-menu" aria-labelledby="' + controlId + '"></ul>', {
				build: function () {
					layout.superclass.build.call(this);
					this.childContainerElement = instance.element.querySelector('.points-companies-list');
					this.events.fire('childcontainerchange', {
						newChildContainerElement: this.childContainerElement,
						oldChildContainerElement: null
					});
				},
				getChildContainerElement: function () {
					return this.childContainerElement;
				},
				clear: function () {
					this.events.fire('childcontainerchange', {
						newChildContainerElement: null,
						oldChildContainerElement: this.childContainerElement
					});
					this.childContainerElement = null;
					layout.superclass.clear.call(this);
				}
			}
		);
		const itemLayout = ymaps.templateLayoutFactory.createClass('<li><a><i><img src="{{data.image}}" alt=""></i> {{data.title}}</a></li>');
		const items = [{name: '', title: 'Перевозчики', image: this.element.dataset.imgAll}, ...companies];
		const listBox = new ymaps.control.ListBox({
			items: items.map(company => new ymaps.control.ListBoxItem({
				data: {
					name: company.name,
					title: company.title,
					image: company.image
				}
			})),
			data: {title: companies.find(company => company.name === this._selectedCompany)?.title || 'Перевозчики'},
			options: {layout, itemLayout},
		});
		listBox.events.add('click', event => {
			const item = event.get('target');
			if (item === listBox) return;
			this._selectCompany(item.data.get('name'));
		});
		this._companyControl = listBox;
		map.controls.add(listBox, {float: 'left'});
	}

	_setPanelDescription(status, point, focusClose = true) {
		const _this = this;
		const container = this._scope.querySelector('[data-points-description]');
		if (!container) return;
		clearTimeout(this._descriptionTimer);

		if (status === 'hide') {
			_completeHide();
		} else {
			_completeShow();
		}

		function build() {
			const content = find('.point-content', container);
			content.innerHTML = '';

			if (status === 'hide') return;

			const p = _this._visiblePoints[Number(point.dataset.index)];

			if (!p) return;

			// Заполняем панель сведениями о пункте выдачи.
			let title;
			if (typeof p.address !== 'undefined' && p.address) {
				title = document.createElement('h4');
				addClass(title, 'title');
				title.innerText = p.address;
			}

			let company = document.createElement('div');
			addClass(company, 'company');

			let span_image = document.createElement('span');
			addClass(span_image, 'image');
			span_image.innerHTML = '<img src="' + p.image + '" alt="">';
			company.append(span_image);

			if (typeof p.title !== 'undefined' && p.title) {
				let span_name = document.createElement('span');
				addClass(span_name, 'name');
				span_name.innerText = p.title;
				company.append(span_name);
			}

			let description;
			if (typeof p.description !== 'undefined' && p.description) {
				description = document.createElement('div');
				addClass(description, 'description');
				description.innerText = p.description;
			}

			let infoList = document.createElement('ul');
			addClass(infoList, 'list list-column list-info');

			const distance = _this._formatDistance(p.distance_meters);
			if (distance) buildLI(_this._origin?.type === 'city' ? 'От населённого пункта' : 'От вашего адреса', distance);

			if ('phone' in p) {
				buildLI('Телефон', p.phone, 'phone');
			}

			if ('email' in p) {
				buildLI('Email', p.email, 'email');
			}

			if ('weight' in p) {
				buildLI('Вес', p.weight);
			}

			if ('postcode' in p) {
				buildLI('Индекс', p.postcode);
			}

			if ('status' in p) {
				buildLI('Статус', p.status);
			}

			if ('office_type' in p) {
				buildLI('Тип отделения', p.office_type);
			}

			if ('services' in p) {
				buildLI('Услуги', p.services);
			}

			if ('worktime' in p) {
				buildLI('Режим работы', p.worktime);
			}

			let btnSubmit = document.createElement('div');
			addClass(btnSubmit, 'btn-submit');

			let button = document.createElement('button');
			addClass(button, 'btn btn-success w-100');
			button.setAttribute('type', 'button');
			button.setAttribute('data-toggle', 'save-point');
			button.setAttribute('data-id', p.id);
			button.setAttribute('data-company', p.company);
			button.setAttribute('data-title', p.title);
			button.setAttribute('data-address', p.address);
			button.innerHTML = _this.params.servicing_offices
				? 'Выбрать отделение'
				: 'Выбрать пункт выдачи';
			btnSubmit.append(button);

			let help = document.createElement('p');
			addClass(help, 'help');
			help.innerHTML = _this.params.servicing_offices
				? 'Индекс и отделение будут сохранены для этого заказа.'
				: 'Он сохранится и будет использоваться по умолчанию при оформлении заказа.';
			btnSubmit.append(help);

			title && content.append(title);
			company && content.append(company);
			description && content.append(description);

			infoList && content.append(infoList);
			btnSubmit && content.append(btnSubmit);

			function buildLI(key, value, type = '') {
				if (!value) return;

				let li = document.createElement('li');
				addClass(li, 'list-item');

				let li_keyword = document.createElement('b');
				li_keyword.innerText = key;
				li.append(li_keyword);

				let values, arr = value.split(';');

				if (arr.length === 1) {
					let item = arr[0];

					if (type === 'phone') {
						values = setPhone(item)
					} else if (type === 'email') {
						values = setEmail(item);
					} else {
						values = document.createElement('span');
						values.innerHTML = item;
					}
				} else {
					values = document.createElement('ul');
					addClass(values, 'list list-column list-info-values');
					addClass(li, 'hasList')

					arr.forEach(item => {
						let li = document.createElement('li');
						addClass(li, 'list-item');

						if (type === 'phone') {
							li.append(setPhone(item))
						} else if (type === 'email') {
							li.append(setEmail(item))
						} else {
							li.innerHTML = item;
						}

						values.append(li);
					});
				}

				li.append(values)
				infoList.append(li);
			}

			function setPhone(phone) {
				let a = document.createElement('a');
				a.setAttribute('href', phoneCall(phone));
				a.innerHTML = phone;

				return a;
			}

			function setEmail(email) {
				let a = document.createElement('a');
				a.setAttribute('href', 'mailto:' + email);
				a.innerHTML = email;

				return a;
			}
		}

		function _completeShow() {
			build();
			addClass(container, 'show');
			addClass(point, 'active');

			_this._descriptionTimer = setTimeout(() => {
				addClass(container, 'slide');
				if (focusClose) container.querySelector('[data-points-close]')?.focus({preventScroll: true});
			}, 30);
		}

		function _completeHide() {
			removeClass(container, 'slide');

			_this._descriptionTimer = setTimeout(() => {
				build();
				removeClass(container, 'show');
			}, 303);
		}
	}
}

export default Points;
